/**
 * @file index.tsx
 *
 * @fileoverview Creates a layour for applicants with a side navigation.
 */
import React, { useContext } from 'react';
import Header from '~/jobiqo/components/Header';
import { useQuery } from '@apollo/client';
import APPLICANT_SIDE_MENU from './side_menu_applicant.gql';
import { MenuItem } from '~/jobiqo/types/types';
import dynamic from 'next/dynamic';
import Footer from '~/jobiqo/components/Footer';
import { ErrorBoundary } from 'react-error-boundary';
import { LayoutMainError } from '~/jobiqo/components/Layout/components/LayoutMainError';
import { FormattedMessage } from 'react-intl';
import { AppContextInterface } from '~/jobiqo/models/app';
import { AppContext } from '~/jobiqo/context/app';
import PrintHide from '~/jobiqo/components/Print/Hide';
import { LayoutContext, LayoutContextInterface } from '~/jobiqo/context/layout';
import LayoutMenuItemWrapper from '~/jobiqo/components/Layout/components/MenuItemWrapper';
import { HideMobile } from '~/vcl/atoms/06-layout/02-visibility/02-hide-mobile';
import { SideBlock } from '~/vcl/organisms/07-navigation/02-side-block';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { HideDesktop } from '~/vcl/atoms/06-layout/02-visibility/01-hide-desktop';
import { BottomMenu } from '~/vcl/organisms/07-navigation/03-bottom-menu';
import { MainWithSideBlock } from '~/vcl/atoms/06-layout/09-main/01-main-width-sideblock';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { HiddenVisually } from '~/vcl/atoms/14-w-a11y/02-visually-hidden';

const Messages = dynamic(() => import('~/jobiqo/components/Messages'), {
  ssr: false,
});

interface ApplicantLayoutProps {
  /**
   * The content to display inside the layout container.
   */
  children: React.ReactNode;
  /**
   * True if the main content should be full width.
   */
  fullWidthContainer?: boolean;
  /**
   * Main content size.
   */
  mainContentSize?: 'small' | 'medium' | 'large' | 'xLarge';
  /**
   * Fixed header.
   *
   * @default false.
   *
   */
  fixedHeader?: boolean;
  /**
   * Hide the site header while keeping the footer visible.
   *
   * @default false.
   */
  hideHeader?: boolean;
}

export const ApplicantLayout = ({
  children,
  fullWidthContainer = false,
  mainContentSize = 'large',
  fixedHeader = false,
  hideHeader = false,
}: ApplicantLayoutProps) => {
  const appContext: AppContextInterface = useContext(AppContext);
  let applicantSideMenu: any = null;

  const {
    getSidenavCollapsed,
    toggleSidenavCollapsed,
  }: LayoutContextInterface = useContext(LayoutContext);

  const { data } = useQuery(APPLICANT_SIDE_MENU, {
    variables: {
      menu: 'side-menu-applicants',
      language: appContext.locale,
    },
  });

  if (data?.applicantSideMenu) {
    applicantSideMenu = data?.applicantSideMenu;
  }

  const collapsed = getSidenavCollapsed();

  return (
    <>
      <HideMobile>
        <SideBlock
          collapsed={collapsed}
          onCollapse={() => {
            toggleSidenavCollapsed();
          }}
        >
          <>
            <Spacing size="large" />
            <nav aria-labelledby="aside-navigation">
              <HiddenVisually id="aside-navigation">
                <FormattedMessage
                  id="aside-navigation"
                  defaultMessage="Aside navigation"
                />
              </HiddenVisually>
              {applicantSideMenu &&
                applicantSideMenu.items.map((menuItem: MenuItem) => (
                  <LayoutMenuItemWrapper
                    key={menuItem.title}
                    menuItem={menuItem}
                    position="side"
                  />
                ))}
            </nav>
          </>
        </SideBlock>
      </HideMobile>
      <HideDesktop>
        <PrintHide>
          <BottomMenu>
            {applicantSideMenu &&
              applicantSideMenu.items.map((menuItem: MenuItem) => (
                <LayoutMenuItemWrapper
                  key={menuItem.title}
                  menuItem={menuItem}
                  position="bottom"
                />
              ))}
          </BottomMenu>
        </PrintHide>
      </HideDesktop>
      {fullWidthContainer ? (
        <MainWithSideBlock sideBlockCollapsed={collapsed}>
          {!hideHeader && <Header fixed={fixedHeader} />}
          <main>
            <Messages fullWidth={true} />
            <ErrorBoundary fallback={<LayoutMainError />}>
              {children}
            </ErrorBoundary>
          </main>
          <Footer />
        </MainWithSideBlock>
      ) : (
        <MainWithSideBlock sideBlockCollapsed={collapsed}>
          {!hideHeader && <Header fixed={fixedHeader} />}
          <Spacing size="large" />
          <main>
            <Main size={mainContentSize}>
              <Spacing size="large" />
              <Messages size={mainContentSize} />
              <Spacing size="large" />
              <ErrorBoundary fallback={<LayoutMainError />}>
                {children}
              </ErrorBoundary>
            </Main>
          </main>
          <Spacing marginTop={[8, 12]} />
          <Footer />
        </MainWithSideBlock>
      )}
    </>
  );
};

export default ApplicantLayout;
